<template>
  <div>
    <div class="edit-salesorder">
      <div class="columns-container">
        <app-button
          v-if="showEditButton"
          :button-class="hasKomNumber ? '' : buttonClass"
          class="quoteActionsButton"
          data-qa="edit-order-button"
          :disabled="hasKomNumber"
          :title="hasKomNumber ? i18n.t('/editsalesorder/locked') : null"
          @click="toggleEditSalesOrderModal(true)"
        >
          <i v-if="hasKomNumber" class="fa-solid fa-lock locked-icon"></i>
          <icon v-else icon="pencil" width="16" height="16" />
          {{ i18n.t('/editsalesorder/editorder') }}
        </app-button>

        <!-- only display this tooltip if there is no kom number -->
        <button v-if="showInfoButton && !hasKomNumber" @click="showInfoModal = true">
          <icon icon="info-plain" large />
        </button>
      </div>

      <app-button
        v-if="showAdditionalButton"
        :button-class="buttonClass"
        class="quoteActionsButton"
        data-qa="additional-order-button"
        @click="clickAdditionalOrderButton"
      >
        {{ i18n.t('/AdditionalOrder/AdditionalOrder') }}
      </app-button>

      <app-button
        v-if="showChangeOrderButton"
        :button-class="buttonClass"
        class="quoteActionsButton"
        data-qa="change-order-button"
        @click="handleChangeOrderClick"
      >
        {{ i18n.t('/Quote/ChangeOrder/ChangeOrderBtn') }}
      </app-button>
    </div>
    <modal
      v-if="showInfoModal"
      :title="i18n.t('/EditSalesOrder/Buttons/InfoModalTitle')"
      @close="showInfoModal = false"
    >
      <p>
        {{ i18n.t('/EditSalesOrder/Buttons/InfoModalText') }}
      </p>
    </modal>
    <confirmation-prompt
      id="edit-order"
      :show="showEditSalesOrderModal"
      :title="i18n.t('/editsalesorder/editsalesorder')"
      :disabled="isLoading"
      @confirm="editSalesOrder"
      @close="toggleEditSalesOrderModal(false)"
    >
      <p>{{ i18n.t('/editsalesorder/confirmeditsalesorder') }}</p>

      <alert type="danger" :text="errorText" />

      <loader v-if="isLoading" />
    </confirmation-prompt>

    <confirmation-prompt
      id="additional-order-prompt"
      :show="showAdditionalOrderConfirmation"
      :title="
        i18n.te('/additionalorder/additionalorderconfirmationtitle')
          ? i18n.t('/additionalorder/additionalorderconfirmationtitle')
          : ''
      "
      @confirm="openAdditionalOrderModal"
      @close="openAdditionalOrderConfirmation(false)"
    >
      <p class="line-break">
        {{ i18n.t('/additionalorder/additionalorderconfirmation') }}
      </p>
    </confirmation-prompt>

    <confirmation-prompt
      id="existing-change-order"
      :show="showConceptDialog"
      :title="'Auto wijzigen'"
      :confirm-button-text="'Bevestigen'"
      :cancel-button-text="'Annuleren'"
      @confirm="onConfirmNavigateToExisting"
      @close="showConceptDialog = false"
    >
      <p v-html="i18n.t('/quote/changeorder/alreadyExistsText')" />
    </confirmation-prompt>
  </div>
</template>

<script>
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'
import quoteOrderService from '@/services/quoteOrderService'
import Alert from '@/components/generic/presentational/atoms/alert/Alert.vue'
import Loader from '@/components/generic/presentational/atoms/loader/Loader.vue'
import Icon from '@/components/generic/presentational/atoms/icon/Icon.vue'
import ConfirmationPrompt from '@/components/generic/presentational/molecules/confirmationPrompt/ConfirmationPrompt.vue'
import { mapGetters } from 'vuex'
import { EventBus } from '@/plugins/event-bus'
import Modal from '@/components/generic/presentational/molecules/modal/Modal.vue'

export default {
  components: {
    Modal,
    AppButton,
    Alert,
    Loader,
    Icon,
    ConfirmationPrompt,
  },
  props: {
    quoteId: {
      type: String,
      default: '',
    },
    showEditButton: {
      type: Boolean,
      default: true,
    },
    showAdditionalButton: {
      type: Boolean,
      default: false,
    },
    showInfoButton: {
      type: Boolean,
      default: false,
    },
    buttonClass: {
      type: String,
      default: 'inverted--bordered',
    },
  },
  emits: ['open-additional-order-modal'],
  data() {
    return {
      orderService: quoteOrderService,
      showEditSalesOrderModal: false,
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      isLoading: false,
      errorText: '',
      showAdditionalOrderConfirmation: false,
      showInfoModal: false,
      showConceptDialog: false,
      existingConceptId: null,
    }
  },
  computed: {
    ...mapGetters({
      isStatusSalesOrder: 'quote/quoteStatusIsSalesOrder',
      signingStatus: 'quote/signingStatus',
      quoteKomNumber: 'quote/komNumber',
      quoteKomYear: 'quote/komYear',
      hasKomNumber: 'quote/hasKomNumber',
      salesOrderSendToAutoline: 'quote/salesOrderSendToAutoline',
      quoteSendToAutoline: 'quote/quoteSendToAutoline',
      dealerSettings: 'user/dealerSettings',
      hasAccessChangeOrders: 'user/hasAccessChangeOrders',
      linkedChangeOrders: 'quote/linkedChangeOrders',
    }),
    showChangeOrderButton() {
      return (
        this.quoteKomNumber &&
        (this.salesOrderSendToAutoline || this.quoteSendToAutoline) &&
        this.dealerSettings.changeOrderEnabled &&
        this.hasAccessChangeOrders
      )
    },
  },
  methods: {
    async editSalesOrder() {
      this.isLoading = true
      try {
        let isSalesOrder = this.isStatusSalesOrder

        if (this.isStatusSalesOrder) {
          await this.orderService.changeSalesOrderToQuote(this.quoteId)
        }
        if (this.signingStatus) {
          await this.orderService.changeSigningStatus(this.quoteId, '')
          this.$store.commit('quote/setSigningStatus', null)
        }

        this.toggleEditSalesOrderModal(false)

        if (isSalesOrder) {
          if (this.$router) {
            this.$router.go()
          } else {
            // only for angular routing
            window.location.reload()
          }
        }
      } catch (e) {
        this.errorText = e.message
      } finally {
        this.isLoading = false
      }
    },
    toggleEditSalesOrderModal(show) {
      if (!show) {
        this.errorText = ''
      }
      this.showEditSalesOrderModal = show
    },
    openAdditionalOrderModal() {
      EventBus.$emit('open-additional-order-modal')
    },
    openAdditionalOrderConfirmation(open) {
      this.showAdditionalOrderConfirmation = open
    },
    clickAdditionalOrderButton() {
      if (this.showEditButton) {
        this.openAdditionalOrderConfirmation(true)
      } else {
        this.openAdditionalOrderModal()
      }
    },
    handleChangeOrderClick() {
      const linkedChangeOrders = this.linkedChangeOrders || []
      const conceptChangeOrder = linkedChangeOrders.find((order) => order.Status === 'CONCEPT')

      if (conceptChangeOrder) {
        this.existingConceptId = conceptChangeOrder.ChangeOrderId
        this.showConceptDialog = true
      } else {
        this.navigateToNewChangeOrder()
      }
    },
    navigateToNewChangeOrder() {
      this.$router.push({
        name: 'new-change-order-page',
        params: {
          komNumber: this.quoteKomNumber,
          komYear: this.quoteKomYear,
        },
        query: {
          quoteId: this.quoteId,
        },
      })
    },
    onConfirmNavigateToExisting() {
      this.showConceptDialog = false
      this.openLinkedChangeOrder(this.existingConceptId)
    },
    openLinkedChangeOrder(changeOrderId) {
      let params = {
        ...this.$route.params,
        changeOrderId: changeOrderId,
        establishmentId: this.$route.params.establishmentId,
      }

      this.$router.push({ name: 'change-order-page', params })
    },
    clickChangeOrderButton() {
      this.handleChangeOrderClick()
    },
  },
}
</script>

<style lang="scss" scoped>
.edit-salesorder {
  :deep(button) {
    display: block;
    margin-left: 0;
  }
}
.columns-container {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
}

.locked-icon {
  margin-right: 0.25em;
}

.quoteActionsButton {
  width: 240px;
}
</style>
